import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { figmaTokens } from "assets/theme/designTokens";
import { AI_MODES, isGeminiOffered } from "utils/assistantAiMode";

const MODE_OPTIONS = [
  {
    id: AI_MODES.RULES,
    icon: "bolt",
    titleKey: "assistant.mode_rules_title",
    titleDefault: "Standard",
    descKey: "assistant.mode_rules_desc",
    descDefault: "Instant answers from portal rules and live data. Recommended for daily use.",
  },
  {
    id: AI_MODES.LOCAL,
    icon: "memory",
    titleKey: "assistant.mode_local_title",
    titleDefault: "Local AI",
    descKey: "assistant.mode_local_desc",
    descDefault: "On-premise Ollama on your server. Private — no data leaves the network. May be slower.",
  },
  {
    id: AI_MODES.GEMINI,
    icon: "cloud",
    titleKey: "assistant.mode_gemini_title",
    titleDefault: "Cloud AI (Gemini)",
    descKey: "assistant.mode_gemini_desc",
    descDefault: "Google Gemini — fast responses. Sanitized text is sent to Google’s API.",
    requiresGemini: true,
  },
];

export default function AssistantAiModeDialog({
  open,
  onClose,
  value,
  onChange,
  t,
  geminiConfigured,
  geminiOnline,
  ollamaOnline,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {t("assistant.mode_dialog_title", "Assistant response mode")}
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1 }}>
        <SoftTypography variant="caption" color="secondary" display="block" mb={1.5}>
          {t(
            "assistant.mode_dialog_hint",
            "Choose how the assistant answers. You can change this anytime."
          )}
        </SoftTypography>
        <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
          {MODE_OPTIONS.map((opt) => {
            if (opt.requiresGemini && !isGeminiOffered()) return null;
            const statusHint =
              opt.id === AI_MODES.GEMINI && geminiConfigured === false
                ? t("assistant.mode_gemini_unconfigured", " · not configured on server")
                : opt.id === AI_MODES.GEMINI && geminiConfigured && geminiOnline === false
                  ? t("assistant.mode_gemini_offline", " · currently unavailable")
                  : opt.id === AI_MODES.LOCAL && ollamaOnline === false
                    ? t("assistant.mode_local_offline", " · Ollama offline")
                    : "";

            return (
              <SoftBox
                key={opt.id}
                mb={1}
                px={1.25}
                py={1}
                borderRadius="12px"
                sx={{
                  border: `1px solid ${value === opt.id ? figmaTokens.accent : figmaTokens.borderSubtle}`,
                  bgcolor: value === opt.id ? "rgba(102, 126, 234, 0.08)" : figmaTokens.pageBg,
                }}
              >
                <FormControlLabel
                  value={opt.id}
                  sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                  control={<Radio size="small" sx={{ mt: 0.25 }} />}
                  label={
                    <SoftBox>
                      <SoftBox display="flex" alignItems="center" gap={0.75} mb={0.25}>
                        <Icon sx={{ fontSize: "1.1rem !important", color: figmaTokens.accent }}>
                          {opt.icon}
                        </Icon>
                        <SoftTypography variant="button" fontWeight="bold">
                          {t(opt.titleKey, opt.titleDefault)}
                          {statusHint ? (
                            <SoftTypography component="span" variant="caption" color="warning">
                              {statusHint}
                            </SoftTypography>
                          ) : null}
                        </SoftTypography>
                      </SoftBox>
                      <SoftTypography variant="caption" color="secondary" sx={{ lineHeight: 1.45 }}>
                        {t(opt.descKey, opt.descDefault)}
                      </SoftTypography>
                    </SoftBox>
                  }
                />
              </SoftBox>
            );
          })}
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: figmaTokens.accent }}>
          {t("assistant.mode_dialog_done", "Done")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AssistantAiModeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  geminiConfigured: PropTypes.bool,
  geminiOnline: PropTypes.bool,
  ollamaOnline: PropTypes.bool,
};
